// Previsão de recebíveis por UC e por data de vencimento.
//
// O que esta tela promete — e o que ela NÃO promete:
//
// Cada linha é "quanto se espera receber desta UC neste vencimento". Metade
// dessas linhas é FATO (o boleto já existe) e metade é ESTIMATIVA (o boleto
// ainda não existe e o valor sai de consumo projetado × tarifa herdada). As
// duas nunca aparecem somadas no mesmo número, porque quem confere caixa trata
// número único como extrato — e três dos quatro insumos aqui são inferidos.
//
// Ver `supabase/functions/_shared/recebivelUc.ts` para o porquê de cada
// inferência e os números de produção que a sustentam.

import React, { useCallback, useMemo, useState } from 'react';
import {
  AlertTriangle,
  ArrowRight,
  CalendarClock,
  ChevronDown,
  Clock,
  Download,
  FileText,
  Filter,
  Handshake,
  RefreshCw,
  Search,
  ShieldCheck,
  Wallet,
  X,
} from 'lucide-react';
import { supabase } from '../../src/lib/supabase';
import { useAgendasRecebiveis } from '../../src/hooks/useAgendasRecebiveis';
import { useAuth } from '../../src/contexts/AuthContext';
import LoadingSpinner from '../../src/components/LoadingSpinner';
import InfoTip from '../../src/components/InfoTip';
import UCManagementModal from './UCManagementModal';
import type { UnidadeConsumidora } from '../../types';
import {
  achatarParaConferencia,
  agregarPorVencimento,
  contarFiltrosAtivos,
  decidirAcao,
  filtrarLinhas,
  opcoesDe,
  FATURADOR_VIGOR,
  FILTROS_VAZIOS,
  NAO_INFORMADO,
  type AcaoRecebivel,
  type FiltrosRecebiveis,
  type ConfiancaRecebivel,
  type LinhaConferencia,
  type TotalMensal,
} from '../../src/lib/recebiveisUc';

const HORIZONTE_MESES = 6;

const brl = (v: number) =>
  v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 2 });

const dataBr = (iso: string | null) =>
  iso ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}` : '—';

// O rótulo e a cor já separam "fato" de "estimativa" (emerald vs. o resto),
// mas dentro da faixa de estimativa (sky/amber/orange) a cor sozinha não é
// suficiente — âmbar e laranja ficam próximos demais para quem bate o olho
// rápido. O ícone é a segunda pista, redundante com a cor, para que a leitura
// não dependa só do matiz.
const CONFIANCA_ESTILO: Record<ConfiancaRecebivel, { rotulo: string; classe: string; Icone: typeof FileText }> = {
  fato: { rotulo: 'Emitido', classe: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300', Icone: FileText },
  alta: { rotulo: 'Previsão firme', classe: 'bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-300', Icone: ShieldCheck },
  media: { rotulo: 'Previsão', classe: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300', Icone: Clock },
  baixa: { rotulo: 'Previsão frágil', classe: 'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-300', Icone: AlertTriangle },
};

/** Data de hoje em ISO — usada para decidir o que já venceu. */
const HOJE_ISO = new Date().toISOString().slice(0, 10);

/** Mesmo estilo de campo do painel de filtros de `UnidadesList`. */
const ESTILO_SELECT =
  'w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 outline-none transition-shadow focus:border-primary focus:shadow-sm placeholder:text-gray-400 dark:border-gray-700 dark:bg-gray-800/60 dark:text-gray-200 dark:placeholder:text-gray-500';

const PrevisaoRecebiveisView: React.FC = () => {
  const { profile } = useAuth();
  // MESMA fonte do Painel Executivo: duas buscas separadas divergiriam em
  // silêncio (paginação, deduplicação, filtro de UC ativa) e a diretoria
  // discutiria com o operacional qual tela está certa.
  const {
    agendas = [],
    empresasParceiras = [],
    ucsDuplicadas = 0,
    loading,
    carregouUmaVez,
    erro: erroCarga,
    recarregar,
  } = useAgendasRecebiveis({ horizonteMeses: HORIZONTE_MESES });
  const [erroAcao, setErroAcao] = useState<string | null>(null);
  const erro = erroCarga ?? erroAcao;
  const [filtros, setFiltros] = useState<FiltrosRecebiveis>(FILTROS_VAZIOS);
  const [filtrosAbertos, setFiltrosAbertos] = useState(false);
  /** UC carregada sob demanda para abrir o modal de gestão. */
  const [ucAberta, setUcAberta] = useState<UnidadeConsumidora | null>(null);
  const [abrindoUc, setAbrindoUc] = useState<number | null>(null);

  const totais: TotalMensal[] = useMemo(() => agregarPorVencimento(agendas), [agendas]);

  const todasAsLinhas = useMemo(() => achatarParaConferencia(agendas), [agendas]);

  const linhas: LinhaConferencia[] = useMemo(
    () => filtrarLinhas(todasAsLinhas, filtros, HOJE_ISO),
    [todasAsLinhas, filtros],
  );

  // As opções saem dos próprios dados — um filtro que oferece valor inexistente
  // devolve lista vazia e o usuário não sabe se filtrou errado ou não há nada.
  const opcoes = useMemo(
    () => ({
      cidade: opcoesDe(todasAsLinhas, (l) => (l.uc.cidade ? `${l.uc.cidade}/${l.uc.estado ?? ''}` : null)),
      concessionaria: opcoesDe(todasAsLinhas, (l) => l.uc.concessionaria),
      grupo: opcoesDe(todasAsLinhas, (l) => l.uc.grupo_tarifario),
      situacao: opcoesDe(todasAsLinhas, (l) => l.uc.situacao),
      status: opcoesDe(todasAsLinhas, (l) => l.statusBoleto),
    }),
    [todasAsLinhas],
  );

  const filtrosAtivos = contarFiltrosAtivos(filtros);
  const setFiltro = useCallback(
    <K extends keyof FiltrosRecebiveis>(campo: K, valor: FiltrosRecebiveis[K]) =>
      setFiltros((f) => ({ ...f, [campo]: valor })),
    [],
  );

  /**
   * Com 13 campos, o badge de número no botão "Filtros" diz QUANTOS estão
   * ativos, mas não O QUÊ — o usuário só descobre abrindo o painel. Estes
   * chips mostram o valor de cada filtro ativo (exceto mês de vencimento, que
   * já tem seu próprio chip ao lado de "Detalhe por UC", e a busca, que já
   * fica visível digitada no campo) com um jeito de remover só aquele.
   */
  const chipsAtivos = useMemo(() => {
    const chips: { chave: string; texto: string; remover: () => void }[] = [];
    if (filtros.origem) {
      chips.push({
        chave: 'origem',
        texto: filtros.origem === 'emitido' ? 'Emitido' : 'Previsto',
        remover: () => setFiltro('origem', ''),
      });
    }
    if (filtros.statusBoleto) {
      chips.push({ chave: 'status', texto: `Status: ${filtros.statusBoleto}`, remover: () => setFiltro('statusBoleto', '') });
    }
    if (filtros.confiancaValor) {
      chips.push({
        chave: 'confiancaValor',
        texto: CONFIANCA_ESTILO[filtros.confiancaValor].rotulo,
        remover: () => setFiltro('confiancaValor', ''),
      });
    }
    if (filtros.certezaMes) {
      chips.push({
        chave: 'certezaMes',
        texto: filtros.certezaMes === 'certo' ? 'Mês confiável' : 'Mês incerto',
        remover: () => setFiltro('certezaMes', ''),
      });
    }
    if (filtros.situacao) {
      chips.push({
        chave: 'situacao',
        texto: filtros.situacao === NAO_INFORMADO ? 'Situação não informada' : filtros.situacao,
        remover: () => setFiltro('situacao', ''),
      });
    }
    if (filtros.cidade) {
      chips.push({
        chave: 'cidade',
        texto: filtros.cidade === NAO_INFORMADO ? 'Localização não informada' : filtros.cidade,
        remover: () => setFiltro('cidade', ''),
      });
    }
    if (filtros.concessionaria) {
      chips.push({
        chave: 'concessionaria',
        texto: filtros.concessionaria === NAO_INFORMADO ? 'Concessionária não informada' : filtros.concessionaria,
        remover: () => setFiltro('concessionaria', ''),
      });
    }
    if (filtros.grupoTarifario) {
      chips.push({
        chave: 'grupo',
        texto: filtros.grupoTarifario === NAO_INFORMADO ? 'Perfil não informado' : filtros.grupoTarifario,
        remover: () => setFiltro('grupoTarifario', ''),
      });
    }
    if (filtros.faturador) {
      const nome =
        filtros.faturador === FATURADOR_VIGOR
          ? 'Vigor Energy (direto)'
          : empresasParceiras.find((e) => e.id === filtros.faturador)?.nome ?? 'Empresa faturadora';
      chips.push({ chave: 'faturador', texto: nome, remover: () => setFiltro('faturador', '') });
    }
    if (filtros.valorMin || filtros.valorMax) {
      const texto =
        filtros.valorMin && filtros.valorMax
          ? `${brl(Number(filtros.valorMin))} – ${brl(Number(filtros.valorMax))}`
          : filtros.valorMin
            ? `A partir de ${brl(Number(filtros.valorMin))}`
            : `Até ${brl(Number(filtros.valorMax))}`;
      chips.push({
        chave: 'valor',
        texto,
        remover: () => {
          setFiltro('valorMin', '');
          setFiltro('valorMax', '');
        },
      });
    }
    if (filtros.somenteComRessalva) {
      chips.push({ chave: 'ressalva', texto: 'Só com ressalva', remover: () => setFiltro('somenteComRessalva', false) });
    }
    if (filtros.somenteAtrasadas) {
      chips.push({ chave: 'atrasada', texto: 'Sem boleto e vencida', remover: () => setFiltro('somenteAtrasadas', false) });
    }
    return chips;
  }, [filtros, empresasParceiras, setFiltro]);

  const ucsComAlerta = useMemo(
    () => agendas.filter((a) => a.avisos.some((x) => x.startsWith('Fatura a conferir'))),
    [agendas],
  );

  /**
   * Leva o operador ao lugar onde a ação acontece.
   *
   * Nenhuma tela do Financeiro aceita deep-link por UC hoje (todas guardam
   * filtro em `useState` local), então o caminho é o que o sistema já usa:
   * o `CustomEvent` que `NotificationDropdown` dispara para abrir a Central de
   * Inadimplência, e o `UCManagementModal` que `UnidadesList` abre com a UC.
   *
   * O modal exige a `UnidadeConsumidora` inteira e aqui só temos o recorte da
   * previsão — por isso a busca sob demanda, no clique, e não no carregamento
   * da tela (seriam 245 UCs completas para talvez zero cliques).
   */
  const abrirUc = useCallback(async (ucId: number) => {
    setAbrindoUc(ucId);
    try {
      const { data, error } = await supabase
        .from('unidades_consumidoras')
        .select('*')
        .eq('id', ucId)
        .maybeSingle();
      if (error) throw error;
      if (data) setUcAberta(data as UnidadeConsumidora);
    } catch (e: any) {
      console.error('Não foi possível abrir a UC:', e);
      setErroAcao(e?.message || 'Não foi possível abrir o cadastro desta UC.');
    } finally {
      setAbrindoUc(null);
    }
  }, []);

  const executarAcao = useCallback(
    async (l: LinhaConferencia, acao: AcaoRecebivel) => {
      // Cobrança tem fluxo próprio e pronto: a Central de Inadimplência abre
      // como modal por cima, então a conferência não perde filtro nem rolagem.
      if (acao.tipo === 'cobrar') {
        window.dispatchEvent(new CustomEvent('vigorhub:open-inadimplencia'));
        return;
      }
      await abrirUc(l.ucId);
    },
    [abrirUc],
  );

  const exportar = useCallback(() => {
    const cab = [
      'UC', 'Cliente', 'Competência', 'Vencimento', 'Data estimada?', 'Origem',
      'Valor do boleto', 'Entra na Vigor', 'kWh',
      // As três confianças vão separadas: quem confere fora do sistema precisa
      // saber se a dúvida é sobre QUANTO ou sobre QUANDO.
      'Confiança no valor', 'Confiança no mês', 'Confiança no dia',
      'Status', 'Avisos',
    ];
    const linhasCsv = linhas.map((l) => [
      l.numeroUc, l.nomeCliente, l.competencia, dataBr(l.vencimento),
      l.vencimentoEstimado ? 'estimada' : 'confirmada', l.origem,
      l.valorBoleto.toFixed(2).replace('.', ','), l.valorVigor.toFixed(2).replace('.', ','),
      String(Math.round(l.kwh)),
      CONFIANCA_ESTILO[l.confiancaValor].rotulo, l.confiancaMes, l.confiancaData,
      l.statusBoleto ?? '', l.avisosDaUc.join(' | '),
    ]);
    const csv = [cab, ...linhasCsv]
      .map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(';'))
      .join('\n');
    const url = URL.createObjectURL(new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `previsao-recebiveis-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [linhas]);

  if (!carregouUmaVez) return <LoadingSpinner label="Montando a previsão de recebíveis..." />;

  // Erro na primeira carga: não há nada útil na tela ainda, então ocupa o
  // espaço inteiro. Erro num "Atualizar" com dados já na tela vira um aviso
  // que não derruba o que o usuário já estava conferindo (ver banner abaixo).
  if (erro && agendas.length === 0) {
    return (
      <div className="p-6">
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-red-800 dark:border-red-900 dark:bg-red-900/20 dark:text-red-300">
          <p className="font-semibold">Não foi possível carregar a previsão.</p>
          <p className="mt-1 text-sm">{erro}</p>
          <button onClick={recarregar} className="mt-3 rounded-lg bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-700">
            Tentar de novo
          </button>
        </div>
      </div>
    );
  }

  return (
    // pb-24: o botão flutuante do assistente fica fixo no canto inferior
    // esquerdo e cobria o número da última/primeira linha visível da tabela.
    <div className="space-y-5 p-4 pb-24 sm:p-6 sm:pb-24">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-xl font-bold text-gray-900 dark:text-white">
            <CalendarClock size={22} className="text-primary" />
            Previsão de recebíveis
            <InfoTip label="Como esta previsão é calculada">
              <p className="mb-2 font-semibold">Metade fato, metade estimativa.</p>
              <p className="mb-2">
                Quando o boleto já existe, o valor e a data são os dele. Quando não existe, o
                valor vem do consumo projetado da UC × a tarifa da última fatura × o desconto
                vigente, e a data é inferida do histórico de vencimentos.
              </p>
              <p>
                Não existe vencimento cadastrado por UC: o boleto segue a fatura da distribuidora.
                O <strong>mês</strong> acerta em 94%; o <strong>dia</strong> é aproximado.
              </p>
            </InfoTip>
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {agendas.length.toLocaleString('pt-BR')} UCs · horizonte de {HORIZONTE_MESES} meses ·
            agrupado por data de vencimento
            {ucsDuplicadas > 0 && (
              <>
                {' · '}
                <span className="text-amber-700 dark:text-amber-400">
                  {ucsDuplicadas} cadastro{ucsDuplicadas > 1 ? 's' : ''} duplicado
                  {ucsDuplicadas > 1 ? 's' : ''} unificado{ucsDuplicadas > 1 ? 's' : ''}
                </span>
                <InfoTip label="O que são os cadastros duplicados unificados" tone="warning">
                  <p className="mb-2">
                    O mesmo cliente aparece cadastrado mais de uma vez, com os dois números de UC
                    trocados entre os registros. Os dois casam com as mesmas faturas.
                  </p>
                  <p>
                    Sem unificar, o recebível dele entraria duas vezes no total — medido em
                    produção, <strong>4,7% do valor da carteira</strong>. A correção do cadastro
                    continua sendo necessária.
                  </p>
                </InfoTip>
              </>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={recarregar}
            disabled={loading}
            aria-busy={loading}
            className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800"
          >
            <RefreshCw size={15} className={loading ? 'animate-spin' : ''} /> {loading ? 'Atualizando...' : 'Atualizar'}
          </button>
          <button
            onClick={exportar}
            disabled={linhas.length === 0}
            className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-3 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <Download size={15} /> Exportar
          </button>
        </div>
      </header>

      {erro && agendas.length > 0 && (
        <div role="alert" className="flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 p-4 dark:border-red-900 dark:bg-red-900/20">
          <AlertTriangle size={20} className="mt-0.5 shrink-0 text-red-600 dark:text-red-400" />
          <div className="text-sm text-red-800 dark:text-red-300">
            <p className="font-semibold">Não foi possível atualizar a previsão.</p>
            <p className="mt-0.5">{erro} Os dados abaixo são da última carga que deu certo.</p>
            <button onClick={recarregar} className="mt-2 font-medium underline hover:no-underline">
              Tentar de novo
            </button>
          </div>
        </div>
      )}

      {ucsComAlerta.length > 0 && (
        <div className="flex items-start gap-3 rounded-xl border border-amber-300 bg-amber-50 p-4 dark:border-amber-800 dark:bg-amber-900/20">
          <AlertTriangle size={20} className="mt-0.5 shrink-0 text-amber-600 dark:text-amber-400" />
          <div className="text-sm text-amber-900 dark:text-amber-200">
            <p className="font-semibold">
              {ucsComAlerta.length} {ucsComAlerta.length === 1 ? 'UC tem fatura' : 'UCs têm faturas'} a conferir
            </p>
            <p className="mt-0.5">
              O valor do boleto não bate com a energia e a tarifa da própria fatura. A previsão
              ignora esses meses, mas eles distorcem qualquer total que os inclua.
            </p>
            <p className="mt-1.5 font-mono text-xs">
              {ucsComAlerta.slice(0, 8).map((a) => a.numeroUc).join(' · ')}
              {ucsComAlerta.length > 8 && ` · +${ucsComAlerta.length - 8}`}
            </p>
          </div>
        </div>
      )}

      {/* ── Totais por mês de vencimento ── */}
      <section>
        <h2 className="mb-0.5 text-sm font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
          Por mês de vencimento
        </h2>
        <p className="mb-2 text-xs text-gray-400 dark:text-gray-500">
          Clique num mês para filtrar o detalhe por UC abaixo.
        </p>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {totais.map((t) => {
            const ativo = filtros.mesVencimento === t.ordinal;
            return (
              <button
                key={t.ordinal}
                onClick={() => setFiltro('mesVencimento', ativo ? null : t.ordinal)}
                aria-pressed={ativo}
                className={`rounded-xl border p-4 text-left shadow-sm transition ${
                  ativo
                    ? 'border-primary bg-primary/5 ring-2 ring-primary/30'
                    : 'border-gray-200 bg-white hover:border-primary/50 dark:border-gray-700 dark:bg-gray-800'
                }`}
              >
                <p className="text-sm font-bold text-gray-900 dark:text-white">{t.rotulo}</p>
                <dl className="mt-2 space-y-1.5 text-sm">
                  <div className="flex items-baseline justify-between gap-2">
                    <dt className="flex items-center gap-1 text-emerald-700 dark:text-emerald-400">
                      <FileText size={13} /> Emitido
                    </dt>
                    <dd className="font-semibold tabular-nums text-gray-900 dark:text-white">
                      {brl(t.emitido.valorBoleto)}
                    </dd>
                  </div>
                  <div className="flex items-baseline justify-between gap-2">
                    <dt className="flex items-center gap-1 text-amber-700 dark:text-amber-400">
                      <CalendarClock size={13} /> Previsto
                    </dt>
                    <dd className="font-semibold tabular-nums text-gray-900 dark:text-white">
                      {brl(t.projetado.valorBoleto)}
                    </dd>
                  </div>
                </dl>
                <p className="mt-2 border-t border-gray-100 pt-2 text-xs text-gray-500 dark:border-gray-700 dark:text-gray-400">
                  {t.emitido.ucs} com boleto · {t.projetado.ucs}{' '}
                  {t.projetado.ucs === 1 ? 'prevista' : 'previstas'}
                  {t.ucsBaixaConfianca > 0 && (
                    <span className="text-orange-600 dark:text-orange-400">
                      {' '}· {t.ucsBaixaConfianca} {t.ucsBaixaConfianca === 1 ? 'frágil' : 'frágeis'}
                    </span>
                  )}
                </p>
              </button>
            );
          })}
        </div>
        {totais.length === 0 && (
          <p className="rounded-xl border border-dashed border-gray-300 p-6 text-center text-sm text-gray-500 dark:border-gray-700 dark:text-gray-400">
            Nenhum recebível no horizonte. Isso acontece quando as UCs ainda não têm faturas
            suficientes para estimar consumo, tarifa e vencimento.
          </p>
        )}
      </section>

      {/* ── Detalhe por UC ── */}
      <section>
        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
            Detalhe por UC
            {filtros.mesVencimento != null && (
              <button
                onClick={() => setFiltro('mesVencimento', null)}
                className="ml-2 rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium normal-case tracking-normal text-primary hover:bg-primary/20"
              >
                {totais.find((t) => t.ordinal === filtros.mesVencimento)?.rotulo} ✕
              </button>
            )}
          </h2>

          <div className="flex flex-wrap items-center gap-2">
            <label className="relative">
              <span className="sr-only">Buscar por UC ou cliente</span>
              <Search size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                value={filtros.busca}
                onChange={(e) => setFiltro('busca', e.target.value)}
                placeholder="Buscar UC ou cliente..."
                className="w-56 rounded-lg border border-gray-300 py-1.5 pl-8 pr-3 text-sm focus:border-primary focus:ring-2 focus:ring-primary/30 dark:border-gray-700 dark:bg-gray-900 dark:text-white"
              />
            </label>

            <button
              onClick={() => setFiltrosAbertos((v) => !v)}
              aria-expanded={filtrosAbertos}
              className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm font-semibold shadow-sm transition-colors ${
                filtrosAbertos || filtrosAtivos > 0
                  ? 'border-primary/30 bg-primary/10 text-primary'
                  : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800/60 dark:text-gray-300 dark:hover:bg-gray-700'
              }`}
            >
              <Filter size={15} />
              Filtros
              {filtrosAtivos > 0 && (
                <span className="flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-primary px-1 text-[10px] font-bold text-white">
                  {filtrosAtivos}
                </span>
              )}
              <ChevronDown size={13} className={`transition-transform ${filtrosAbertos ? 'rotate-180' : ''}`} />
            </button>

            <span className="whitespace-nowrap text-xs text-gray-400">
              {linhas.length.toLocaleString('pt-BR')} de {todasAsLinhas.length.toLocaleString('pt-BR')} linhas
            </span>

            {(filtrosAtivos > 0 || filtros.busca) && (
              <button
                onClick={() => setFiltros(FILTROS_VAZIOS)}
                className="inline-flex items-center gap-1 text-xs font-semibold text-gray-500 transition-colors hover:text-red-500 dark:text-gray-400"
              >
                <X size={13} /> Limpar
              </button>
            )}
          </div>
        </div>

        {chipsAtivos.length > 0 && (
          <div className="mb-3 flex flex-wrap items-center gap-1.5">
            {chipsAtivos.map((c) => (
              <button
                key={c.chave}
                onClick={c.remover}
                aria-label={`Remover filtro: ${c.texto}`}
                className="inline-flex items-center gap-1 rounded-full border border-primary/30 bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary transition-colors hover:bg-primary/20"
              >
                {c.texto}
                <X size={11} />
              </button>
            ))}
          </div>
        )}

        {filtrosAbertos && (
          <div className="mb-3 grid animate-fade-in grid-cols-2 gap-3 rounded-xl border border-gray-200 bg-gray-50/60 p-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 dark:border-gray-700 dark:bg-gray-800/40">
            <select
              aria-label="Mês de vencimento"
              value={filtros.mesVencimento ?? ''}
              onChange={(e) => setFiltro('mesVencimento', e.target.value === '' ? null : Number(e.target.value))}
              className={ESTILO_SELECT}
            >
              <option value="">Mês de vencimento (todos)</option>
              {totais.map((t) => (
                <option key={t.ordinal} value={t.ordinal}>{t.rotulo}</option>
              ))}
            </select>

            <select
              aria-label="Emitido ou previsto"
              value={filtros.origem}
              onChange={(e) => setFiltro('origem', e.target.value as FiltrosRecebiveis['origem'])}
              className={ESTILO_SELECT}
            >
              <option value="">Emitido ou previsto (todos)</option>
              <option value="emitido">Emitido (boleto existe)</option>
              <option value="projetado">Previsto (estimativa)</option>
            </select>

            <select
              aria-label="Status do boleto"
              value={filtros.statusBoleto}
              onChange={(e) => setFiltro('statusBoleto', e.target.value)}
              className={ESTILO_SELECT}
            >
              <option value="">Status do boleto (todos)</option>
              {opcoes.status.filter((s) => s !== NAO_INFORMADO).map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>

            <select
              aria-label="Confiança no valor"
              value={filtros.confiancaValor}
              onChange={(e) => setFiltro('confiancaValor', e.target.value as FiltrosRecebiveis['confiancaValor'])}
              className={ESTILO_SELECT}
            >
              <option value="">Confiança no valor (todas)</option>
              <option value="fato">Emitido (fato)</option>
              <option value="alta">Previsão firme</option>
              <option value="media">Previsão</option>
              <option value="baixa">Previsão frágil</option>
            </select>

            <select
              aria-label="Certeza do mês de vencimento"
              value={filtros.certezaMes}
              onChange={(e) => setFiltro('certezaMes', e.target.value as FiltrosRecebiveis['certezaMes'])}
              className={ESTILO_SELECT}
            >
              <option value="">Confiança no mês (todas)</option>
              <option value="certo">Confiável</option>
              <option value="incerto">Incerto</option>
            </select>

            <select
              aria-label="Situação da UC"
              value={filtros.situacao}
              onChange={(e) => setFiltro('situacao', e.target.value)}
              className={ESTILO_SELECT}
            >
              <option value="">Situação da UC (todas)</option>
              {opcoes.situacao.map((s) => (
                <option key={s} value={s}>{s === NAO_INFORMADO ? 'Não informada' : s}</option>
              ))}
            </select>

            <select
              aria-label="Localização"
              value={filtros.cidade}
              onChange={(e) => setFiltro('cidade', e.target.value)}
              className={ESTILO_SELECT}
            >
              <option value="">Localização (todas)</option>
              {opcoes.cidade.map((c) => (
                <option key={c} value={c}>{c === NAO_INFORMADO ? 'Não informada' : c}</option>
              ))}
            </select>

            <select
              aria-label="Concessionária"
              value={filtros.concessionaria}
              onChange={(e) => setFiltro('concessionaria', e.target.value)}
              className={ESTILO_SELECT}
            >
              <option value="">Concessionária (todas)</option>
              {opcoes.concessionaria.map((c) => (
                <option key={c} value={c}>{c === NAO_INFORMADO ? 'Não informada' : c}</option>
              ))}
            </select>

            <select
              aria-label="Perfil de consumo"
              value={filtros.grupoTarifario}
              onChange={(e) => setFiltro('grupoTarifario', e.target.value)}
              className={ESTILO_SELECT}
            >
              <option value="">Perfil de consumo (todos)</option>
              {opcoes.grupo.map((g) => (
                <option key={g} value={g}>{g === NAO_INFORMADO ? 'Não informado' : g}</option>
              ))}
            </select>

            {profile?.role === 'adm' && (
              <select
                aria-label="Empresa faturadora"
                value={filtros.faturador}
                onChange={(e) => setFiltro('faturador', e.target.value)}
                className={ESTILO_SELECT}
              >
                <option value="">Empresa faturadora (todas)</option>
                <option value={FATURADOR_VIGOR}>Vigor Energy (direto)</option>
                {empresasParceiras.map((ep) => (
                  <option key={ep.id} value={ep.id}>{ep.nome}</option>
                ))}
              </select>
            )}

            <input
              type="number"
              min={0}
              inputMode="decimal"
              aria-label="Valor mínimo do boleto"
              placeholder="Valor mín. (R$)"
              value={filtros.valorMin}
              onChange={(e) => setFiltro('valorMin', e.target.value)}
              className={ESTILO_SELECT}
            />
            <input
              type="number"
              min={0}
              inputMode="decimal"
              aria-label="Valor máximo do boleto"
              placeholder="Valor máx. (R$)"
              value={filtros.valorMax}
              onChange={(e) => setFiltro('valorMax', e.target.value)}
              className={ESTILO_SELECT}
            />

            <label className="flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 dark:border-gray-700 dark:bg-gray-800/60 dark:text-gray-200">
              <input
                type="checkbox"
                checked={filtros.somenteComRessalva}
                onChange={(e) => setFiltro('somenteComRessalva', e.target.checked)}
                className="rounded border-gray-300 text-primary focus:ring-primary/40"
              />
              <span className="inline-flex items-center gap-1">
                Só com ressalva
                <InfoTip label="O que é uma ressalva">
                  <p>
                    A UC tem algum aviso pendente: fatura que não bate com a própria energia e
                    tarifa, cadastro sem desconto informado, tarifa defasada, ou cadastro
                    duplicado. É o mesmo aviso que aparece no ícone ao lado do número da UC.
                  </p>
                </InfoTip>
              </span>
            </label>

            <label className="flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 dark:border-gray-700 dark:bg-gray-800/60 dark:text-gray-200">
              <input
                type="checkbox"
                checked={filtros.somenteAtrasadas}
                onChange={(e) => setFiltro('somenteAtrasadas', e.target.checked)}
                className="rounded border-gray-300 text-primary focus:ring-primary/40"
              />
              <span className="inline-flex items-center gap-1">
                Sem boleto e vencida
                <InfoTip label="O que é sem boleto e vencida" tone="warning">
                  <p>
                    A fatura desta competência deveria ter vencido e <strong>não existe boleto
                    nenhum</strong>. Não é inadimplência — é a conta da distribuidora que não
                    entrou, e ninguém está cobrando esse cliente.
                  </p>
                </InfoTip>
              </span>
            </label>
          </div>
        )}

        <div className="overflow-x-auto rounded-xl border border-gray-200 shadow-sm dark:border-gray-700">
          <table className="min-w-full divide-y divide-gray-200 text-sm dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-800">
              <tr>
                <th scope="col" className="px-3 py-2.5 text-left font-semibold text-gray-700 dark:text-gray-200">UC / Cliente</th>
                <th scope="col" className="px-3 py-2.5 text-left font-semibold text-gray-700 dark:text-gray-200">Competência</th>
                <th scope="col" className="px-3 py-2.5 text-left font-semibold text-gray-700 dark:text-gray-200">Vencimento</th>
                <th scope="col" className="px-3 py-2.5 text-right font-semibold text-gray-700 dark:text-gray-200">Valor do boleto</th>
                <th scope="col" className="px-3 py-2.5 text-right font-semibold text-gray-700 dark:text-gray-200">
                  <span className="inline-flex items-center gap-1">
                    Entra na Vigor
                    <InfoTip label="Diferença entre o boleto e o que entra na Vigor">
                      <p>
                        Em UC de empresa parceira o boleto cai na conta da Vigor, mas só o % da
                        Vigor sobre a tarifa cheia é dela: a parte da parceira sai por split e a da
                        Equatorial paga a conta. Nas UCs da Vigor os dois valores são iguais.
                      </p>
                    </InfoTip>
                  </span>
                </th>
                <th scope="col" className="px-3 py-2.5 text-left font-semibold text-gray-700 dark:text-gray-200">Situação</th>
                <th scope="col" className="px-3 py-2.5 text-right font-semibold text-gray-700 dark:text-gray-200">
                  <span className="inline-flex items-center gap-1">
                    Próxima ação
                    <InfoTip label="Como a próxima ação é decidida">
                      <p className="mb-2">
                        A ação sai do estado da linha, na ordem do que custa mais caro deixar parado:
                      </p>
                      <ul className="list-disc space-y-1 pl-4">
                        <li><strong>Cobrar</strong> — o boleto venceu e não foi pago.</li>
                        <li><strong>Ver por quê</strong> — deveria ter boleto e não tem.</li>
                        <li><strong>Conferir fatura</strong> — o boleto não bate com a própria fatura.</li>
                        <li><strong>Corrigir cadastro</strong> — falta tarifa ou desconto.</li>
                      </ul>
                    </InfoTip>
                  </span>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white dark:divide-gray-800 dark:bg-gray-900">
              {linhas.slice(0, 300).map((l) => (
                <tr key={`${l.ucId}-${l.ordinal}`} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                  {/* max-w: sem largura máxima o `truncate` do nome não age e um
                      cliente de razão social longa empurra a tabela para fora do
                      laptop (medido: 1231px de tabela em 1166px de área). */}
                  <td className="max-w-[300px] px-3 py-2">
                    <div className="flex items-center gap-1.5">
                      {/* A navegação genérica vive AQUI, não na coluna de ação:
                          um botão "Abrir UC" em toda linha caía em 91,7% delas e
                          apagava o sinal da coluna. Sublinhado pontilhado discreto
                          para não competir com os botões urgentes. */}
                      <button
                        onClick={() => abrirUc(l.ucId)}
                        disabled={abrindoUc === l.ucId}
                        title={`Abrir o cadastro e o histórico da UC ${l.numeroUc}`}
                        className="font-mono text-xs text-gray-900 underline decoration-dotted decoration-gray-400 underline-offset-2 transition-colors hover:text-primary hover:decoration-primary disabled:opacity-60 dark:text-white dark:decoration-gray-500"
                      >
                        {l.numeroUc}
                      </button>
                      {l.deParceira && (
                        <Handshake size={13} className="text-violet-500" aria-label="UC de empresa parceira" />
                      )}
                      {l.avisosDaUc.length > 0 && (
                        <InfoTip label={`Ressalvas da UC ${l.numeroUc}`} tone="warning">
                          <ul className="list-disc space-y-1 pl-4">
                            {l.avisosDaUc.map((a, i) => (
                              <li key={i}>{a}</li>
                            ))}
                          </ul>
                        </InfoTip>
                      )}
                    </div>
                    <p className="truncate text-xs text-gray-500 dark:text-gray-400" title={l.nomeCliente}>
                      {l.nomeCliente}
                    </p>
                  </td>
                  <td className="px-3 py-2 text-gray-700 dark:text-gray-300">{l.competencia}</td>
                  <td className="px-3 py-2">
                    <span className="text-gray-900 dark:text-white">{dataBr(l.vencimento)}</span>
                    {l.vencimentoEstimado && (
                      // A precisão da DATA é própria dela: o mês acerta em 94%,
                      // o dia em 33%. Dizer "estimado" sem dizer o quanto faria
                      // o usuário confiar no dia tanto quanto no mês.
                      <span className="block text-xs text-gray-500 dark:text-gray-400">
                        {l.confiancaMes === 'baixa'
                          ? 'mês incerto'
                          : l.confiancaData === 'alta'
                            ? 'dia previsível'
                            : l.janelaDias && l.janelaDias.min !== l.janelaDias.max
                              ? `mês certo · dia varia (${l.janelaDias.min}–${l.janelaDias.max})`
                              : 'data aproximada'}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right font-semibold tabular-nums text-gray-900 dark:text-white">
                    {brl(l.valorBoleto)}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    <span className={l.valorVigor < l.valorBoleto ? 'font-semibold text-violet-700 dark:text-violet-300' : 'text-gray-600 dark:text-gray-400'}>
                      {brl(l.valorVigor)}
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    {(() => {
                      {/* O badge fala do VALOR. A confiança da data vive na
                          coluna Vencimento, porque o dia não muda quanto entra. */}
                      const { rotulo, classe, Icone } = CONFIANCA_ESTILO[l.confiancaValor];
                      return (
                        <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${classe}`}>
                          <Icone size={11} />
                          {rotulo}
                        </span>
                      );
                    })()}
                    {l.statusBoleto && (
                      <span className="ml-1 text-xs text-gray-500 dark:text-gray-400">{l.statusBoleto}</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {(() => {
                      const acao = decidirAcao(l, HOJE_ISO);
                      // Linha resolvida (paga/quitada) não ganha botão: numa
                      // tela de centenas de linhas, um botão em toda linha
                      // deixa de sinalizar o que precisa de atenção.
                      if (!acao) return <span className="text-xs text-gray-400 dark:text-gray-500">—</span>;
                      const ocupado = abrindoUc === l.ucId;
                      return (
                        <div className="inline-flex items-center gap-1">
                          <button
                            onClick={() => executarAcao(l, acao)}
                            disabled={ocupado}
                            title={acao.motivo}
                            aria-label={`${acao.rotulo} — ${l.numeroUc} ${l.nomeCliente}`}
                            className={`inline-flex items-center gap-1 whitespace-nowrap rounded-lg border px-2.5 py-1 text-xs font-semibold shadow-sm transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${
                              acao.urgente
                                ? 'border-red-200 bg-red-50 text-red-700 hover:bg-red-100 dark:border-red-900 dark:bg-red-900/25 dark:text-red-300 dark:hover:bg-red-900/40'
                                : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700'
                            }`}
                          >
                            {ocupado ? <RefreshCw size={12} className="animate-spin" /> : <ArrowRight size={12} />}
                            {acao.rotulo}
                          </button>
                          {/* `title` sozinho não abre por teclado nem em toque — o
                              motivo da ação também vive aqui, no mesmo padrão de
                              InfoTip do resto da tela. */}
                          <InfoTip label={`Por que "${acao.rotulo}" — ${l.numeroUc}`} tone={acao.urgente ? 'danger' : 'default'}>
                            <p>{acao.motivo}</p>
                          </InfoTip>
                        </div>
                      );
                    })()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {linhas.length === 0 && (
            <div className="p-6 text-center text-sm text-gray-500 dark:text-gray-400">
              <p>Nenhuma linha bate com os filtros aplicados.</p>
              {(filtrosAtivos > 0 || filtros.busca) && (
                <button
                  onClick={() => setFiltros(FILTROS_VAZIOS)}
                  className="mt-2 font-medium text-primary underline hover:no-underline"
                >
                  Limpar filtros
                </button>
              )}
            </div>
          )}
          {linhas.length > 300 && (
            <p className="border-t border-gray-200 bg-gray-50 p-3 text-center text-xs text-gray-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400">
              Mostrando 300 de {linhas.length.toLocaleString('pt-BR')} linhas. Use a busca ou o
              filtro de mês, ou exporte para ver tudo.
            </p>
          )}
        </div>
      </section>

      <footer className="flex items-start gap-2 rounded-xl bg-gray-50 p-4 text-xs text-gray-600 dark:bg-gray-800/50 dark:text-gray-400">
        <Wallet size={15} className="mt-0.5 shrink-0" />
        <p>
          <strong>Emitido</strong> é fato: o boleto existe, com valor e vencimento próprios.{' '}
          <strong>Previsto</strong> é estimativa: o boleto ainda não existe, o valor sai do consumo
          projetado com a tarifa da última fatura, e a data vem do histórico de vencimentos da UC —
          o mês acerta em 94% dos casos, o dia é aproximado. Os dois nunca são somados no mesmo
          número.
        </p>
      </footer>

      {/* O mesmo modal que `UnidadesList` abre ao clicar numa linha — a UC é
          buscada inteira no clique, porque a previsão só carrega o recorte. */}
      <UCManagementModal
        isOpen={!!ucAberta}
        onClose={() => setUcAberta(null)}
        unidade={ucAberta}
        onSave={() => {
          setUcAberta(null);
          recarregar();
        }}
      />
    </div>
  );
};

export default PrevisaoRecebiveisView;
